var { OPERATOR_LABELS, normalizeConditionRule, conditionHasEffectiveValue, operatorRequiresValue } = require('./conditionRule');

// Truncate a string for display inside a small tile subtitle slot.
function trunc(text, max) {
  var s = String(text == null ? '' : text);
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

// Render a single condition row as plain text, e.g. "@Articles.length > 0".
function formatCondition(cond) {
  if (!cond) return '';
  var op = OPERATOR_LABELS[cond.operator] || cond.operator || '';
  var lhs = String(cond.lhs || cond.left || cond.subject || '').trim();
  var needsValue = operatorRequiresValue(cond.operator);
  var rhs = needsValue ? String(cond.value || '').trim() : '';
  return [lhs, op, rhs].filter(function(p) { return p && p.length; }).join(' ');
}

// Build a meaningful subtitle for a node based on its type + params.
// Falls back to the original subtitle/displayTitle when no smarter value is available.
function getNodeSubtitle(props) {
  var icon = props.icon;
  var category = String(props.category || '').toLowerCase();
  var paramValues = props.paramValues || {};
  var fallback = props.subtitle || '';

  // Custom nodes use the title to convey purpose (e.g. "Reply to Customer").
  // The default "Custom node" subtitle is redundant noise on the tile, so we
  // hide it. If a custom node has been given a more meaningful subtitle, the
  // template author can pass anything other than "Custom node" / empty and
  // it will still show.
  if (category === 'custom') {
    var fb = String(fallback || '').trim().toLowerCase();
    if (!fb || fb === 'custom node') return '';
    return fallback;
  }

  // Condition: show the rule itself, e.g. "@Articles.length > 0".
  // When the rule isn't configured yet, render NO subtitle — the static
  // "Route by yes/no rule" fallback is redundant noise on the tile (the
  // title already says "Condition"), matching the set-variable behavior.
  if (icon === 'condition') {
    var rule = normalizeConditionRule(props.conditionRule);
    var active = (rule.conditions || []).filter(conditionHasEffectiveValue);
    if (active.length === 0) return '';
    var first = formatCondition(active[0]);
    if (active.length === 1) return trunc(first, 28);
    var logic = String(rule.logic || 'and').toUpperCase();
    return trunc(first + ' +' + (active.length - 1) + ' ' + logic, 28);
  }

  // Set Variable: show "var = value" once configured, otherwise nothing.
  // The default "Update a variable at runtime" fallback is redundant noise on
  // the tile (the title already says "Save X"), so we hide it until the user
  // has actually picked a variable.
  if (icon === 'set-variable') {
    var varName = String(paramValues.var || paramValues.varName || '').trim();
    var val = paramValues.value;
    var valStr = (val === undefined || val === null) ? '' : (typeof val === 'object' ? JSON.stringify(val) : String(val));
    // Append/merge modes use a different operator glyph so the tile telegraphs
    // "this adds to a list" vs "this overwrites" without opening the sidebar.
    var mode = String(paramValues.mode || 'replace').toLowerCase();
    var op = mode === 'append' ? ' += ' : (mode === 'merge' ? ' ⊕ ' : ' = ');
    if (varName && valStr) return trunc(varName + op + valStr, 26);
    if (varName) return trunc(varName + op + '...', 26);
    return '';
  }

  // Variable: show current value (truncated)
  if (icon === 'variables') {
    var v = paramValues.value;
    if (v === undefined || v === null || v === '') return fallback || 'no value';
    var vStr = typeof v === 'object' ? JSON.stringify(v) : String(v);
    return trunc(vStr, 26);
  }

  // Sleep: show "1000 ms"
  if (icon === 'sleep') {
    var dur = paramValues.duration !== undefined ? paramValues.duration : 1000;
    var unit = String(paramValues.unit || 'ms');
    return String(dur) + ' ' + unit;
  }

  // Loop: show iteration mode
  if (icon === 'loop') {
    return paramValues.mode ? String(paramValues.mode) : 'each item';
  }

  // Console Log: show what's being logged (truncated). When nothing is logged
  // yet, render NO subtitle — the static fallback is redundant noise on the
  // tile (the title already says "Console.Log"), matching set-variable/condition.
  if (icon === 'console-log') {
    var msg = paramValues.value || paramValues.message || paramValues.input;
    if (msg !== undefined && msg !== null && String(msg).length) {
      return trunc(typeof msg === 'object' ? JSON.stringify(msg) : String(msg), 28);
    }
    return '';
  }

  // File Preview: show filename if known
  if (icon === 'file-preview') {
    var fname = paramValues.filename || paramValues.name || paramValues.path;
    if (fname) return trunc(String(fname).split(/[\\/]/).pop(), 26);
    return fallback || 'file preview';
  }

  // Agent: show model or first line of system prompt
  if (icon === 'agent') {
    if (paramValues.model) return trunc(String(paramValues.model), 26);
    var sys = paramValues.system || paramValues.prompt;
    if (sys) return trunc(String(sys).split('\n')[0], 26);
    return fallback || 'AI agent';
  }

  return fallback;
}

module.exports = {
  getNodeSubtitle: getNodeSubtitle
};
